import { IconButton } from "@mui/material";
import FocusIcon from "@mui/icons-material/CenterFocusWeak";
import { useTranslate } from "ra-core";
import { useSnapshot } from "valtio";

import viewerState from "stores/viewer";
import { statusColor, rgbCss } from "./microbleedStatus";
import { focusMicrobleed } from "./microbleedPoints";
import type { Microbleed } from "./useMicrobleeds";

// Re-export so existing imports (e.g. MicrobleedLegend) keep working.
export { statusColor } from "./microbleedStatus";

interface Props {
  microbleed: Microbleed;
  displayId: string;
}

function MicrobleedRow({ microbleed, displayId }: Props) {
  const t = useTranslate();
  const { focusedMarkerCode } = useSnapshot(viewerState);

  const color = statusColor(microbleed.status);
  const isFocused = focusedMarkerCode === microbleed.uid;
  const structure = microbleed.bodySiteDisplay ?? "Unknown";
  const canFocus = !!microbleed.coordinateMm || !!microbleed.coordinateVoxel;

  const handleFocus = () => {
    if (!microbleed.coordinateMm && !microbleed.coordinateVoxel) return;
    viewerState.focusedMarkerCode = microbleed.uid;
    // Jump to the SAME world position the dot was placed at. Fallback prefers
    // the grid-free mm coordinate (RAS->LPS), then the exact-volume voxel.
    focusMicrobleed(microbleed.uid, {
      mm: microbleed.coordinateMm,
      voxel: microbleed.coordinateVoxel,
      refVolumeId: (viewerState as any).cmbSpatialVolumeId,
    });
  };

  return (
    <tr className="shadow-inner bg-stone-700">
      <td className="py-2 w-10" />
      <td className="py-2 text-center w-10">
        {canFocus ? (
          <IconButton
            size="small"
            title={t("patient.view.tooltip.marker.focus")}
            aria-label={t("patient.view.tooltip.marker.focus")}
            onClick={handleFocus}
          >
            <FocusIcon
              style={isFocused ? { color: rgbCss(color) } : undefined}
            />
          </IconButton>
        ) : null}
      </td>
      <td className="py-2 text-left">{displayId}</td>
      <td className="py-2 text-center italic text-red-400 font-semibold">
        {microbleed.status === "new" ? "NEW" : ""}
      </td>
      <td className="py-2 text-left first-letter:uppercase">{structure}</td>
    </tr>
  );
}

export default MicrobleedRow;
